import React, { useContext, useEffect, useState } from "react";
import { Button, Icon, Input, message, Select } from "antd";
import style from "./index.module.scss";
import { EditableContext, isRequiredAtom } from "../../store";
import FieldWithError from "components/FieldWithError";
import {
    RuleModelItem,
    ruleItemMap,
    AllConfig,
    SelectMap,
    jsonRegex
} from "service/score";
import useModalHandler from "utils/useModalHandler";
import AdvancedSetting from "./Components/AdvancedSetting";
import { useAtomValue, useSetAtom } from "jotai";
import InputNumber from "./Components/InputNumber";
import DetailSettingModal from "./Components/DetailSettingModal";
import { disabledAtom } from "../../../../store";
import { useParams } from "react-router-dom";

interface EditableCellProps {
    title: React.ReactNode;
    editable: boolean;
    children: React.ReactNode;
    dataIndex: keyof RuleModelItem;
    record: RuleModelItem;
    handleSave: (record: RuleModelItem) => void;
}
const { Option } = Select;
const { TextArea } = Input;

const EditableCell: React.FC<EditableCellProps> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    ...restProps
}) => {
    const { courseNo, courseSeq } = useParams<StringMap>();

    const disabled = useAtomValue(disabledAtom({ courseNo, courseSeq }));
    const form = useContext(EditableContext)!;
    const modal = useModalHandler();
    const detailInfoModal = useModalHandler();
    const [detailTitle, setDetailTitle] = useState("明细设置");

    const [signType, setSignType] = useState("");
    const [rulesConfigObj, setRulesConfigObj] = useState({
        title: "",
        type: ""
    });

    const setIsRequired = useSetAtom(isRequiredAtom);
    useEffect(() => {
        if (!record) return;
        setSignType(record.description);
    }, [record]);

    //出勤统计
    const [obj, setObj] = useState<AllConfig>({});

    useEffect(() => {
        if (
            record &&
            !!record.formulaModeJson &&
            jsonRegex.test(record.formulaModeJson)
        ) {
            setObj(JSON.parse(record!.formulaModeJson));
        } else {
            setObj({});
        }
    }, [record]);

    //正常保存
    const save = async () => {
        try {
            const values = await form.validateFields();
            handleSave({ ...record, ...values });
            setIsRequired(false);
        } catch (errInfo) {
            message.error("有必填项未输入");
            setIsRequired(true);
        }
    };

    //保存签到的成绩细则
    const saveFormulaModeObj = async (obj: AllConfig) => {
        try {
            handleSave({ ...record, formulaModeJson: JSON.stringify(obj) });
        } catch (errInfo) {
            message.error("有必填项未输入");
        }
    };

    const onSelect = (e: string) => {
        setSignType(e);
        save();
    };

    //重置导致record发生变化
    useEffect(() => {
        if (!record) return;

        form.setFieldsValue({
            ruleName: record.ruleName,
            weight: record.weight,
            description: record.description,
            formulaModeJson: record.formulaModeJson,
            remark: record.remark
        });
    }, [record]);

    //高级设置
    const handleAdvanced = () => {
        modal.open();
        if (!record.flag) return;
        setRulesConfigObj({
            title: SelectMap[record.flag]?.title?.[record.description],
            type: record.description
        });
    };

    //明细分配
    const handleDetailSetting = (record: RuleModelItem) => {
        detailInfoModal.open();
        setDetailTitle(record.ruleName);
    };

    function inputNodeRender() {
        if (dataIndex === "weight")
            return (
                <InputNumber
                    value={record[dataIndex]}
                    onPressEnter={save}
                    onBlur={save}
                    disabled={disabled}
                    precision={1}
                />
            );

        if (dataIndex === "description" && record.flag) {
            if (
                [
                    "CoursewareStudyCalculator",
                    "ExamScoreCalculator",
                    "DailyExamCalculator",
                    "DiscussScoreCalculator"
                ].includes(record.flag)
            ) {
                return (
                    <div
                        className={style.desp}
                        style={{
                            color: disabled
                                ? "rgba(0,0,0,.25)"
                                : "rgba(0,0,0,.65)"
                        }}>
                        {record.description}
                    </div>
                );
            } else if (["CustomCalculator"].includes(record.flag)) {
                return (
                    <TextArea
                        onPressEnter={save}
                        onBlur={save}
                        className={style.input}
                        disabled={disabled}
                        maxLength={1000}
                        style={{ resize: "none" }}
                        autoSize={{ minRows: 1, maxRows: 5 }}
                    />
                );
            } else {
                return (
                    <Select
                        placeholder="请选择"
                        className={style.select}
                        value={signType}
                        onSelect={(e: string) => onSelect(e)}
                        onBlur={save}
                        disabled={disabled}>
                        {record.flag &&
                            SelectMap[record.flag]?.rulesSelectOption?.map(
                                (item: { label: string; value: string }) => (
                                    <Option value={item.value} key={item.value}>
                                        {item.label}
                                    </Option>
                                )
                            )}
                    </Select>
                );
            }
        }

        if (dataIndex === "formulaModeJson" && record.flag) {
            if (
                ["CoursewareStudyCalculator", "CustomCalculator"].includes(
                    record.flag
                )
            ) {
                return;
            } else {
                return (
                    <div className={style.formulaColumn}>
                        {record.flag !== "ExamScoreCalculator" && (
                            <>
                                <span>
                                    {record.flag &&
                                        SelectMap[record.flag]?.description?.[
                                            record.description
                                        ]}
                                </span>
                                &nbsp;&nbsp;&nbsp;
                                <Button type="link" onClick={handleAdvanced}>
                                    <Icon type="setting" />
                                    高级设置
                                </Button>
                            </>
                        )}
                        {record.flag === "ExamScoreCalculator" && (
                            <span>
                                默认将全部考试的平均成绩按比例折算成得分，请在明细分配中选出期末考试并分配权重
                                &nbsp;&nbsp;&nbsp;
                            </span>
                        )}
                        {(record.description.includes("平均分") ||
                            record.flag === "ExamScoreCalculator") && (
                            <Button
                                type="link"
                                onClick={() => handleDetailSetting(record)}>
                                <Icon type="profile" />
                                明细分配
                            </Button>
                        )}
                    </div>
                );
            }
        }

        return (
            <TextArea
                onPressEnter={save}
                onBlur={save}
                className={style.input}
                disabled={disabled}
                maxLength={1000}
                style={{ resize: "none" }}
                autoSize={{ minRows: 1, maxRows: 5 }}
            />
        );
    }
    let childNode = children;

    if (editable) {
        childNode = (
            <>
                <FieldWithError
                    name={dataIndex}
                    rules={[
                        {
                            required: !["formulaModeJson", "remark"].includes(
                                dataIndex
                            ),
                            message: `${
                                ruleItemMap[
                                    dataIndex as keyof typeof ruleItemMap
                                ]
                            }必填`
                        }
                    ]}>
                    {inputNodeRender()}
                </FieldWithError>
            </>
        );
    }

    return (
        <td {...restProps}>
            {childNode}

            {modal.render(
                <AdvancedSetting
                    visible={modal.visible}
                    onCancel={modal.close}
                    obj={obj}
                    setObj={setObj}
                    onSave={saveFormulaModeObj}
                    rulesConfigObj={rulesConfigObj}
                    flag={record?.flag}
                />
            )}

            {detailInfoModal.render(
                <DetailSettingModal
                    detailTitle={detailTitle}
                    visible={detailInfoModal.visible}
                    onCancel={detailInfoModal.close}
                    obj={obj}
                    setObj={setObj}
                    onSave={saveFormulaModeObj}
                    type={record?.flag}
                />
            )}
        </td>
    );
};

export default EditableCell;
